import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, ChevronLeft, ChevronRight } from "lucide-react";
import { istToday } from "@/lib/dateFormat";

const fmt = (n) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const p2r = (v) => v || 0;  // Values are already in rupees from API
const fmtDate = (d) => { if (!d || d.length < 10) return d || ""; const p = d.slice(0,10).split("-"); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : d; };

// FOLLOW-UP 2 (7): only the order's own four payment status words. Overdue is its own column.
const STATUS_COLORS = { "Fully Paid": "bg-emerald-100 text-emerald-800", "Partially Paid": "bg-amber-100 text-amber-800", "Unpaid": "bg-zinc-100 text-zinc-700", "Short Paid": "bg-orange-100 text-orange-800" };

export default function ReceivablesPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "all");
  const [data, setData] = useState({ summary: {}, orders: [], pagination: { page: 1, page_size: 50, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const today = istToday();
  const monthStart = today.slice(0, 8) + "01";
  const [dateFrom, setDateFrom] = useState(searchParams.get("from") || monthStart);
  const [dateTo, setDateTo] = useState(searchParams.get("to") || today);
  const [sortBy, setSortBy] = useState("days_overdue");
  const [sortOrder, setSortOrder] = useState("desc");

  const applyQuick = (preset) => {
    const now = new Date();
    if (preset === "this_month") { setDateFrom(today.slice(0, 8) + "01"); setDateTo(today); }
    else if (preset === "last_month") { const d = new Date(now.getFullYear(), now.getMonth() - 1, 1); const e = new Date(now.getFullYear(), now.getMonth(), 0); setDateFrom(d.toISOString().split("T")[0]); setDateTo(e.toISOString().split("T")[0]); }
    else if (preset === "this_quarter") { const q = Math.floor(now.getMonth() / 3) * 3; setDateFrom(`${now.getFullYear()}-${String(q + 1).padStart(2, "0")}-01`); setDateTo(today); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { tab, from_date: dateFrom, to_date: dateTo, sort_by: sortBy, sort_order: sortOrder, page, page_size: 50 };
      const { data: d } = await api.get("/reports/receivables", { params });
      setData(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [tab, dateFrom, dateTo, sortBy, sortOrder, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTabChange = (t) => { setTab(t); setPage(1); setSearchParams({ tab: t }); };

  const handleSort = (col) => {
    if (sortBy === col) { setSortOrder(sortOrder === "desc" ? "asc" : "desc"); }
    else { setSortBy(col); setSortOrder("desc"); }
    setPage(1);
  };

  const handleExport = () => {
    const params = new URLSearchParams({ tab, from_date: dateFrom, to_date: dateTo, sort_by: sortBy, sort_order: sortOrder, export: "excel" });
    api.get(`/reports/receivables?${params}`, { responseType: "blob" }).then(r => {
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `Receivables_${tab}_${today}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Exported");
    }).catch(() => toast.error("Export failed"));
  };

  const s = data.summary || {};
  const at = s.all_time || {};
  const ageing = at.ageing || {};
  const orders = data.orders || [];
  const pag = data.pagination || {};
  const totalPages = Math.ceil((pag.total || 0) / (pag.page_size || 50));

  const SortHeader = ({ col, label, className = "" }) => (
    <TableHead className={`cursor-pointer hover:bg-zinc-100 select-none ${className}`} onClick={() => handleSort(col)}>
      {label} {sortBy === col && <span className="text-blue-600">{sortOrder === "desc" ? " ↓" : " ↑"}</span>}
    </TableHead>
  );

  return (
    <div className="space-y-4" data-testid="receivables-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Payment Receivables</h1>
        <Button variant="outline" className="h-8 text-xs" onClick={handleExport} data-testid="export-receivables"><Download className="w-3.5 h-3.5 mr-1" />Export Excel</Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-200" data-testid="receivables-tabs">
        {[
          { key: "all", label: "All", count: (s.order_count || 0) },
          { key: "outstanding", label: "Outstanding", count: (s.outstanding_count || 0) },
          { key: "overdue", label: "Overdue", count: (s.overdue_count || 0) },
          { key: "paid", label: "Fully Paid", count: (s.paid_count || 0) },
        ].map(t => (
          <button key={t.key} onClick={() => handleTabChange(t.key)} className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${tab === t.key ? "border-blue-600 text-blue-700" : "border-transparent text-zinc-500 hover:text-zinc-700"}`} data-testid={`tab-${t.key}`}>
            {t.label} <Badge variant="outline" className="ml-1 text-[10px] h-4 px-1">{t.count}</Badge>
          </button>
        ))}
      </div>

      {/* Date filters */}
      <div className="flex flex-wrap gap-2 items-center text-xs">
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-7 w-32 text-xs" />
        <span className="text-zinc-400">to</span>
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-7 w-32 text-xs" />
        {["this_month", "last_month", "this_quarter"].map(p => (
          <Button key={p} variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={() => applyQuick(p)}>{p.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}</Button>
        ))}
      </div>

      {/* KPI Strip */}
      <p className="text-[10px] uppercase tracking-wide text-zinc-400">Outstanding, overdue and the ageing buckets below are all-time; invoiced, received, CN and deductions follow the date filter</p>
      <div className="grid grid-cols-6 gap-3" data-testid="receivables-kpi-strip">
        <div className="border border-zinc-200 p-2 rounded-sm text-center"><p className="text-[9px] text-zinc-400 uppercase">Total Invoiced</p><p className="font-mono font-bold text-sm">{fmt(p2r(s.total_invoiced))}</p></div>
        <div className="border border-green-200 bg-green-50 p-2 rounded-sm text-center"><p className="text-[9px] text-green-600 uppercase">Received against these invoices</p><p className="font-mono font-bold text-sm text-green-700">{fmt(p2r(s.total_received))}</p></div>
        <div className="border border-blue-200 bg-blue-50 p-2 rounded-sm text-center"><p className="text-[9px] text-blue-600 uppercase">Settled by CN</p><p className="font-mono font-bold text-sm text-blue-700">{fmt(p2r(s.total_cn_settled))}</p></div>
        <div className="border border-zinc-200 bg-zinc-50 p-2 rounded-sm text-center"><p className="text-[9px] text-zinc-500 uppercase">Deductions</p><p className="font-mono font-bold text-sm text-zinc-700">{fmt(p2r(s.total_deductions))}</p></div>
        <div className="border border-amber-200 bg-amber-50 p-2 rounded-sm text-center"><p className="text-[9px] text-amber-600 uppercase">Total Outstanding</p><p className="font-mono font-bold text-sm text-amber-700" data-testid="kpi-outstanding">{fmt(p2r(at.outstanding ?? s.total_outstanding))}</p></div>
        <div className="border border-red-200 bg-red-50 p-2 rounded-sm text-center"><p className="text-[9px] text-red-600 uppercase">Total Overdue</p><p className="font-mono font-bold text-sm text-red-700" data-testid="kpi-overdue">{fmt(p2r(at.overdue ?? s.total_overdue))}</p></div>
      </div>

      <div className="grid grid-cols-6 gap-2" data-testid="receivables-ageing-strip">
        <div className="border border-green-200 bg-green-50 p-2 rounded-sm text-center"><p className="text-[9px] text-green-700 uppercase">Not yet due</p><p className="font-mono font-bold text-xs text-green-800" data-testid="ageing-not-due">{fmt(p2r(at.not_due))}</p></div>
        {["1-15", "16-30", "31-60", "61-90", "90+"].map(b => (
          <div key={b} className="border border-zinc-200 p-2 rounded-sm text-center">
            <p className="text-[9px] text-zinc-500 uppercase">{b} days</p>
            <p className="font-mono font-bold text-xs text-zinc-800" data-testid={`ageing-${b}`}>{fmt(p2r(ageing[b]))}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-auto" data-testid="receivables-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 text-[10px]">
              <SortHeader col="order_number" label="Order #" className="w-24" />
              <TableHead className="w-20">PO #</TableHead>
              <TableHead className="w-28">Invoice #</TableHead>
              <SortHeader col="invoice_date" label="Inv Date" className="w-20" />
              <TableHead className="w-22">Delivery Date</TableHead>
              <SortHeader col="customer_name" label="Customer" />
              <TableHead className="w-28">Channel</TableHead>
              <TableHead className="w-24">Trade Format</TableHead>
              <TableHead className="w-20">Sales Rep</TableHead>
              <SortHeader col="invoice_total" label="Invoice" className="text-right w-24" />
              <TableHead className="text-right w-20">Deductions</TableHead>
              <TableHead className="text-right w-24">Net Recv</TableHead>
              <SortHeader col="cash_received" label="Received" className="text-right w-24" />
              <SortHeader col="outstanding" label="Outstanding" className="text-right w-24" />
              <TableHead className="w-20">Terms</TableHead>
              <SortHeader col="due_date" label="Due Date" className="w-22" />
              <SortHeader col="credit_start_date" label="Credit Start" className="w-28" />
              <SortHeader col="credit_days_applied" label="Credit Days" className="w-20" />
              <SortHeader col="days_overdue" label="Days Overdue" className="w-28" />
              <TableHead className="text-[10px] uppercase w-36">Flags</TableHead>
              <TableHead className="w-24">Days Since Del.</TableHead>
              <TableHead className="w-20">Payment Status</TableHead>
              <TableHead className="w-24">Overdue</TableHead>
              <TableHead className="w-22">Last Pay</TableHead>
              <TableHead className="text-right w-20">Last Amt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={21} className="text-center py-8 text-zinc-400">Loading...</TableCell></TableRow>
            ) : orders.length === 0 ? (
              <TableRow><TableCell colSpan={21} className="text-center py-8 text-zinc-400">No orders found</TableCell></TableRow>
            ) : orders.map((r, i) => (
              <TableRow key={i} className={`text-[11px] ${r.is_overdue ? "bg-red-50" : (r.payment_status === "Fully Paid" ? "bg-emerald-50/40" : "")}`} data-testid={`recv-row-${r.order_number}`}>
                <TableCell><Link to={`/orders/${r.order_id}`} className="text-blue-600 hover:underline font-mono">{r.order_number}</Link></TableCell>
                <TableCell className="text-zinc-500">{r.po_number || "—"}</TableCell>
                <TableCell className="font-mono text-[10px]">{r.invoice_number || "—"}</TableCell>
                <TableCell>{fmtDate(r.invoice_date)}</TableCell>
                <TableCell>{r.delivery_date ? fmtDate(r.delivery_date) : "—"}</TableCell>
                <TableCell className="font-medium truncate max-w-[140px]">{r.customer_name}</TableCell>
                <TableCell className="text-[10px] truncate max-w-[100px]">{r.channel_name}</TableCell>
                <TableCell className="text-[10px]">{r.trade_format}</TableCell>
                <TableCell className="text-[10px]">{r.sales_rep}</TableCell>
                <TableCell className="text-right font-mono">{fmt(p2r(r.invoice_total))}</TableCell>
                <TableCell className="text-right font-mono text-red-500">{r.deductions > 0 ? `-${fmt(p2r(r.deductions))}` : "—"}</TableCell>
                <TableCell className="text-right font-mono">{fmt(p2r(r.net_receivable))}</TableCell>
                <TableCell className="text-right font-mono text-green-600">{r.cash_received > 0 ? fmt(p2r(r.cash_received)) : "—"}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{r.outstanding > 0 ? fmt(p2r(r.outstanding)) : "0.00"}</TableCell>
                <TableCell className="text-[10px]">{r.payment_terms}</TableCell>
                <TableCell data-testid={`due-date-${r.order_number}`}>{fmtDate(r.due_date) || <span className="text-zinc-400">Not delivered</span>}</TableCell>
                <TableCell className="text-[10px]" data-testid={`credit-start-${r.order_number}`}>
                  {r.credit_start_date ? <>{fmtDate(r.credit_start_date)}<span className="block text-[9px] text-zinc-400">{r.credit_start_source}</span></> : "—"}
                </TableCell>
                <TableCell className="text-[10px] font-mono text-center">{r.credit_days_applied || 0}</TableCell>
                <TableCell>
                  {r.outstanding <= 0 ? <span className="text-zinc-400">Paid</span> :
                   !r.due_date ? <span className="text-zinc-400">No due date</span> :
                   r.days_overdue > 0 ? <span className="text-red-600 font-semibold" data-testid={`overdue-days-${r.order_number}`}>{r.days_overdue}d overdue</span> :
                   <span className="text-green-600">Not due</span>}
                </TableCell>
                <TableCell className="text-[10px]">
                  {(r.flags || []).length === 0 ? <span className="text-zinc-300">—</span> :
                    (r.flags || []).map(f => (
                      <span key={f} className="block text-amber-700 font-semibold" data-testid={`flag-${r.order_number}`}>{f}</span>
                    ))}
                </TableCell>
                <TableCell className="text-[10px] font-mono">{r.days_since_delivery !== "" && r.days_since_delivery !== null && r.days_since_delivery !== undefined ? `${r.days_since_delivery}d` : "—"}</TableCell>
                <TableCell><span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[r.payment_status] || "bg-zinc-100"}`} data-testid={`recv-status-${r.order_number}`}>{r.payment_status}</span></TableCell>
                <TableCell className={r.is_overdue ? "text-red-700 font-semibold text-[10px]" : "text-zinc-500 text-[10px]"} data-testid={`recv-overdue-${r.order_number}`}>{r.overdue_label}</TableCell>
                <TableCell className="text-[10px]">{fmtDate(r.last_payment_date) || "—"}</TableCell>
                <TableCell className="text-right font-mono text-[10px]">{r.last_payment_amount > 0 ? fmt(p2r(r.last_payment_amount)) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span>Showing {orders.length} of {pag.total || 0} orders (page {pag.page || 1} of {totalPages || 1})</span>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" className="h-6 text-[10px]" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-3 h-3" /></Button>
          <Button variant="outline" size="sm" className="h-6 text-[10px]" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-3 h-3" /></Button>
        </div>
      </div>
    </div>
  );
}
